import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
    Mountain, Map, Code, Radar, Brain, CheckCircle2, Circle,
    BookOpen, Database, GraduationCap, ChevronRight, Award,
} from "lucide-react";

const ICONS = { mountain: Mountain, map: Map, code: Code, radar: Radar, brain: Brain };
const RES_ICON = { reference: BookOpen, paper: BookOpen, course: GraduationCap, dataset: Database };

export default function LearningPath({ onChange }) {
    const [stages, setStages] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [activeStage, setActiveStage] = useState(null);
    const [answers, setAnswers] = useState({});

    useEffect(() => {
        api.get("/learning-path").then(({ data }) => {
            setStages(data);
            setActiveStage(data[0]);
        });
        api.get("/progress").then(({ data }) => setCompleted(data.completed || []));
    }, []);

    const pct = stages.length ? Math.round((completed.length / stages.length) * 100) : 0;

    const toggleComplete = async (stageId) => {
        const isDone = completed.includes(stageId);
        const { data } = await api.post("/progress", { stage_id: stageId, completed: !isDone });
        setCompleted(data.completed || []);
        toast.success(isDone ? "Stage reopened" : "Stage completed! 🎉");
        onChange && onChange();
    };

    const quizResult = (stage) => {
        const total = stage.quiz.length;
        let correct = 0;
        stage.quiz.forEach((q, i) => { if (answers[`${stage.id}-${i}`] === q.answer) correct++; });
        return { correct, total };
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
            {/* Stage nav */}
            <aside className="rounded-xl border border-ares-border bg-ares-dark/90 p-5">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="font-heading text-lg font-medium">Learning Path</h2>
                    <Award className="w-5 h-5 text-ares-amber" />
                </div>
                <p className="font-mono text-[11px] text-ares-muted mb-3">{completed.length} of {stages.length} stages complete</p>
                <Progress value={pct} className="h-2 bg-ares-panel mb-5" data-testid="learning-progress-bar" />

                <div className="space-y-2">
                    {stages.map((s) => {
                        const Icon = ICONS[s.icon] || BookOpen;
                        const done = completed.includes(s.id);
                        return (
                            <button key={s.id} onClick={() => setActiveStage(s)} data-testid={`stage-item-${s.id}`}
                                className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${activeStage?.id === s.id ? "border-ares-orange/50 bg-ares-orange/10" : "border-ares-border bg-ares-panel hover:border-ares-orange/30"}`}>
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${done ? "bg-ares-emerald/15 text-ares-emerald" : "bg-ares-void text-ares-muted"}`}>
                                    {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4.5 h-4.5" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-mono text-[10px] uppercase tracking-wider text-ares-muted">Stage {s.order}</p>
                                    <p className="text-sm font-medium truncate">{s.title}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-ares-muted shrink-0" />
                            </button>
                        );
                    })}
                </div>
            </aside>

            {/* Stage detail */}
            {activeStage && (
                <motion.section key={activeStage.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-ares-border bg-ares-dark/90 p-6" data-testid="stage-detail">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2">
                                <Badge className="bg-ares-orange/15 text-ares-orange border border-ares-orange/40">Stage {activeStage.order}</Badge>
                                <span className="font-mono text-[11px] text-ares-muted">{activeStage.duration}</span>
                            </div>
                            <h2 className="font-heading text-2xl font-semibold mt-2">{activeStage.title}</h2>
                            <p className="text-sm text-ares-muted mt-2 max-w-2xl">{activeStage.summary}</p>
                        </div>
                        <Button onClick={() => toggleComplete(activeStage.id)}
                            className={completed.includes(activeStage.id) ? "bg-ares-emerald/15 text-ares-emerald border border-ares-emerald/40 hover:bg-ares-emerald/25" : "bg-ares-orange hover:bg-[#e55a2b] text-white"}
                            data-testid="toggle-stage-complete-btn">
                            {completed.includes(activeStage.id) ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Completed</> : <><Circle className="w-4 h-4 mr-2" /> Mark Complete</>}
                        </Button>
                    </div>

                    {/* Objectives */}
                    <div className="mt-6">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ares-cyan mb-3">Learning Objectives</p>
                        <ul className="space-y-2">
                            {activeStage.objectives.map((o, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-ares-bright/90">
                                    <CheckCircle2 className="w-4 h-4 text-ares-cyan mt-0.5 shrink-0" /> {o}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Resources */}
                    <div className="mt-6">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ares-amber mb-3">Resources</p>
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            {activeStage.resources.map((r, i) => {
                                const RIcon = RES_ICON[r.type] || BookOpen;
                                return (
                                    <div key={i} className="flex items-center gap-2.5 rounded-lg border border-ares-border bg-ares-panel p-3">
                                        <RIcon className="w-4 h-4 text-ares-amber shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm truncate">{r.label}</p>
                                            <p className="font-mono text-[10px] uppercase tracking-wider text-ares-muted">{r.type}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quiz */}
                    <div className="mt-6 rounded-lg border border-ares-border bg-ares-panel p-5">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ares-emerald mb-4">Knowledge Check</p>
                        <div className="space-y-5">
                            {activeStage.quiz.map((q, qi) => {
                                const key = `${activeStage.id}-${qi}`;
                                const chosen = answers[key];
                                return (
                                    <div key={qi}>
                                        <p className="text-sm font-medium mb-2.5">{qi + 1}. {q.q}</p>
                                        <div className="grid sm:grid-cols-2 gap-2">
                                            {q.options.map((opt, oi) => {
                                                const isChosen = chosen === oi;
                                                const isCorrect = oi === q.answer;
                                                let cls = "border-ares-border bg-ares-dark hover:border-ares-emerald/40";
                                                if (chosen !== undefined && isChosen) cls = isCorrect ? "border-ares-emerald bg-ares-emerald/15 text-ares-emerald" : "border-destructive bg-destructive/10 text-destructive";
                                                else if (chosen !== undefined && isCorrect) cls = "border-ares-emerald/50 bg-ares-emerald/10 text-ares-emerald";
                                                return (
                                                    <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [key]: oi }))}
                                                        className={`text-left text-sm rounded-lg border px-3 py-2.5 transition-colors ${cls}`}
                                                        data-testid={`quiz-${activeStage.id}-q${qi}-opt${oi}`}>
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {activeStage.quiz.every((_, i) => answers[`${activeStage.id}-${i}`] !== undefined) && (
                            <div className="mt-4 pt-4 border-t border-ares-border">
                                {(() => { const { correct, total } = quizResult(activeStage); return (
                                    <p className="font-mono text-sm text-ares-emerald" data-testid="quiz-score">Score: {correct}/{total} correct</p>
                                ); })()}
                            </div>
                        )}
                    </div>
                </motion.section>
            )}
        </div>
    );
}
